import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Table,
  Input,
  Select,
  Tag,
  Avatar,
  Space,
  Progress,
  Pagination,
  Empty,
  Tooltip,
} from "antd";
import { HomeOutlined, UserOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { Breadcrumb } from "react-bootstrap";
import { fetchCreatedTasks } from "@/store/tasksApi/tasksThunk";
import formatDate from "@/utils/formatDate";
import truncateChars from "@/utils/cutWords";
import { MEDIA_BASE_URL } from "../api/api";
import { FileOutlined, PictureOutlined } from "@ant-design/icons";


const { Search } = Input;
const { Option } = Select;

const statusMap = {
  pending: { label: "Kutilmoqda", color: "default" },
  in_progress: { label: "Jarayonda", color: "orange" },
  submitted: { label: "Tasdiqlashda", color: "blue" },
  approved: { label: "Tasdiqlangan", color: "green" },
  rejected: { label: "Qaytarilgan", color: "red" },
  pending_after_rejection: { label: "Rad etilgan", color: "volcano" },
};

const TasksListCreated = () => {
  const dispatch = useDispatch();
  const { createdTasks, status } = useSelector((state) => state.tasksApi);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    dispatch(fetchCreatedTasks({ page, page_size: 30, status: statusFilter || null }));
  }, [dispatch, page, statusFilter]);

  const filtered = createdTasks?.items?.filter((task) =>
    search ? task.title.toLowerCase().includes(search.toLowerCase()) : true
  );

  const columns = [
    {
      title: "№",
      render: (_, __, index) => (page - 1) * 30 + index + 1,
      width: 60,
    },
    {
      title: "Nomi",
      dataIndex: "title",
      render: (text, record) => (
        <p>
          {truncateChars(text, 160)}
        </p>
      ),
    },
    {
      title: "Tavsif",
      dataIndex: "description",
      render: (text) => truncateChars(text, 11500),
    },
    {
      title: "Yaratgan foydalanuvchi",
      dataIndex: "created_by",
      render: (creator) => (
         <div className="d-flex align-items-center">
            <Avatar
              src={creator.picture}
              size={40}
              style={{ marginRight: 8 }}
            />
            <div>
              <div style={{ fontWeight: 600 }}>{creator.first_name}</div>
              <div style={{ fontSize: 12, color: "#666" }}>
                {creator.surname}  
              </div>
            </div>
          </div>
      ),
    },
     {
      title: "Mas’ul shaxs",
      key: "approver_info",
      render: (_, record) => {
        const approver = record.approver_info;
        return approver ? (
          <div className="d-flex align-items-center">
            <Avatar
              src={approver.picture}
              size={40}
              style={{ marginRight: 8 }}
            />
            <div>
              <div style={{ fontWeight: 600 }}>{approver.first_name}</div>
              <div style={{ fontSize: 12, color: "#666" }}>
                {approver.surname}  
              </div>
            </div>
          </div>
        ) : (
          <span>—</span>
        );
      },
    },
    {
      title: "Biriktirilgan foydalanuvchilar",
      dataIndex: "assigned_users",
      render: (users) => {
        if (!users?.length) return "-";

        const statusColor = {
          pending: "#facc15",     // 🟡 sariq
          in_progress: "#2563eb", // 🔵 ko‘k
          submitted: "#a855f7",   // 🟣 binafsha
          approved: "#16a34a",    // 🟢 yashil
          rejected: "#dc2626",    // 🔴 qizil
        };

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {users.map((user, index) => (
              <Tooltip
                key={user.id}
                title={`${user?.first_name || ""} ${user?.surname || ""} (${statusMap[user.task_status]?.label || user.task_status})`}
              >
                <div
                  style={{
                    position: "relative",
                    marginLeft: index === 0 ? 0 : -10, // 🔁 stack effekti
                    zIndex: users.length - index, // yuqoridagisi ustida chiqadi
                  }}
                >
                  <Avatar
                    src={user.picture}
                    icon={!user.picture && <UserOutlined />}
                    size={40}
                    style={{
                      border: "2px solid white",
                      borderRadius: "50%",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                      cursor: "pointer",
                      backgroundColor: "#f3f4f6",
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      bottom: 0,
                      right: 2, // 👈 chap pastda holat belgisi
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      border: "2px solid white",
                      backgroundColor: statusColor[user.task_status] || "#d1d5db",
                    }}
                  />
                </div>
              </Tooltip>
            ))}
          </div>
        );
      },
    }
    ,

    {
      title: "Muddati",
      dataIndex: "deadline",
      render: (date) => formatDate(date),
    },
    {
      title: "Rad etilgan",
      key: "rejection_info",
      render: (_, record) => {
        if (!record.was_rejected) return "-";

        return (
          <Tooltip
            title={
              <div>
                <div><strong>Rad etilgan:</strong> {formatDate(record.last_rejection_at)}</div>
                {record.last_rejection_reason && (
                  <div style={{ marginTop: 4 }}>
                    <strong>Sabab:</strong> {record.last_rejection_reason}
                  </div>
                )}
              </div>
            }
          >
            <Tag color="red" style={{ cursor: "help" }}>
              🔄 Rad etilgan
            </Tag>
          </Tooltip>
        );
      },
    },
    // {
    //   title: "Fayl va rasm",
    //   render: (_, record) => {
    //     const imageUrl = record.image
    //       ? `${record.image}`
    //       : null;
    //     const fileUrl = record.file
    //       ? `${record.file}`
    //       : null;

    //     return (
    //       <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    //         {/* 🖼 Agar rasm bo‘lsa */}
    //         {imageUrl && (
    //           <a
    //             href={MEDIA_BASE_URL + imageUrl}
    //             target="_blank"
    //             rel="noopener noreferrer"
    //             style={{
    //               display: "inline-block",
    //               borderRadius: 8,
    //               overflow: "hidden",
    //               boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    //             }}
    //           >
    //             <img
    //               src={MEDIA_BASE_URL + imageUrl}
    //               alt="task"
    //               style={{
    //                 width: 45,
    //                 height: 45,
    //                 objectFit: "cover",
    //                 display: "block",
    //               }}
    //             />
    //           </a>
    //         )}

    //         {/* 📎 Fayl yuklab olish tugmasi */}
    //         {fileUrl && (
    //           <a
    //             href={fileUrl}
    //             download
    //             style={{
    //               backgroundColor: "#2563eb",
    //               color: "white",
    //               padding: "6px 10px",
    //               borderRadius: 6,
    //               fontSize: 13,
    //               textDecoration: "none",
    //               boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    //             }}
    //           >
    //             📥 Yuklab olish
    //           </a>
    //         )}
    //       </div>
    //     );
    //   },
    // },
    {
      title: "Fayl va rasm",
      render: (_, record) => {
        const imageUrl = record.image || null;
        const fileUrl = record.file || null;

        if (!imageUrl && !fileUrl) return "-";

        return (
          <div style={{ display: "flex", gap: 10 }}>
            {/* 🖼 Rasmni yuklab olish */}
            {imageUrl && (
              <Tooltip title="Rasmni yuklab olish">
                <a
                  href={MEDIA_BASE_URL + imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    border: "1.8px solid #16a34a",
                    color: "#16a34a",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#16a34a";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#16a34a";
                  }}
                >
                  <PictureOutlined style={{ fontSize: 18 }} />
                </a>
              </Tooltip>
            )}

            {/* 📄 Faylni yuklab olish */}
            {fileUrl && (
              <Tooltip title="Faylni yuklab olish">
                <a
                  href={MEDIA_BASE_URL + fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    border: "1.8px solid #2563eb",
                    color: "#2563eb",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#2563eb";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#2563eb";
                  }}
                >
                  <FileOutlined style={{ fontSize: 18 }} />
                </a>
              </Tooltip>
            )}
          </div>
        );
      },
    },


  ];


  console.log(createdTasks);


  return (
    <div>
      <Breadcrumb className="py-2">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>Yaratilgan topshiriqlar</Breadcrumb.Item>
      </Breadcrumb>

      <Card
        title="Yaratilgan vazifalar ro‘yxati"
        bordered={false}
        style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderRadius: 12 }}
        extra={
          <Space>
            <Search
              placeholder="Qidiruv..."
              allowClear
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Holat"
              allowClear
              onChange={(val) => setStatusFilter(val)}
              style={{ width: 140 }}
            >
              {Object.entries(statusMap).map(([key, value]) => (
                <Option key={key} value={key}>
                  {value.label}
                </Option>
              ))}
            </Select>
          </Space>
        }
      >
        {status === "loading" ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>Yuklanmoqda...</div>
        ) : filtered?.length ? (
          <>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filtered}
              pagination={false}
              bordered
              size="middle"
            />
            <div style={{ textAlign: "right", marginTop: 16 }}>
              <Pagination
                current={page}
                total={createdTasks.total}
                pageSize={30}
                onChange={(p) => setPage(p)}
                showSizeChanger={false}
              />
            </div>
          </>
        ) : (
          <Empty description="Ma'lumot topilmadi" />
        )}
      </Card>
    </div>
  );
};

export default TasksListCreated;
